// ---------------------------------------------------------------------------
// OpenSCAD source code updating and value validation
// ---------------------------------------------------------------------------

import type { Parameter, RangeValue } from '@shared/types';
import { scadStringToHex, scadStringToAlpha } from '../colorUtils';
import { serializeMaterialDef } from './materials';

// ---------------------------------------------------------------------------
// String escape helpers
// ---------------------------------------------------------------------------

export function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function escapeReplacement(string: string) {
  return string.replace(/\$/g, '$$$$');
}

export function escapeQuotes(string: string) {
  return string.replace(/"/g, '\\"');
}

/**
 * Re-escapes real newline/tab/carriage-return characters back to their
 * OpenSCAD escape sequences so the serialized SCAD source remains valid.
 * Must be applied before escapeQuotes (backslashes are handled first).
 */
export function escapeNewlines(string: string) {
  return string
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/\t/g, '\\t')
    .replace(/\r/g, '\\r');
}

// ---------------------------------------------------------------------------
// Multi-line assignment joining (used by updateParameter)
// ---------------------------------------------------------------------------

/**
 * Collapse multi-line variable assignments onto a single line.
 *
 * The updater expects `name = value;` on one line.
 * A multi-line array literal like:
 *   layout = [
 *     ["a", "b"],
 *     ["c", "d"]
 *   ]; // grid: 2x2 [...]
 * spans several lines, causing single-line regexes to miss it entirely.
 *
 * Algorithm: walk lines. When a line looks like an assignment start
 * (`name =`) but has no `;` yet, accumulate subsequent lines (joined
 * with a space) until the closing `;` is found. Pass-through lines
 * that already contain their `;` are untouched — no cost for the
 * common case of existing single-line parameters.
 */
export function joinMultiLineAssignments(code: string): string {
  const rawLines = code.split('\n');
  const assignmentStartRe = /^\s*\$?[a-zA-Z_][a-zA-Z0-9_]*\s*=/;
  const joinedLines: string[] = [];
  let accumulating = false;
  let accumulated = '';

  for (const rawLine of rawLines) {
    if (accumulating) {
      accumulated += ' ' + rawLine.trim();
      if (rawLine.includes(';')) {
        joinedLines.push(accumulated);
        accumulating = false;
        accumulated = '';
      }
    } else {
      if (assignmentStartRe.test(rawLine) && !rawLine.includes(';')) {
        accumulating = true;
        accumulated = rawLine.trimEnd();
      } else {
        joinedLines.push(rawLine);
      }
    }
  }
  if (accumulating && accumulated) {
    joinedLines.push(accumulated);
  }

  return joinedLines.join('\n');
}

// ---------------------------------------------------------------------------
// Value validation
// ---------------------------------------------------------------------------

/**
 * Validates and sanitizes a parameter value.
 */
export function validateParameterValue(
  param: Parameter,
  value: Parameter['value'],
): Parameter['value'] {
  if (param.type === 'number' || param.type === 'angle' || !param.type) {
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return Number(param.defaultValue);
    }
    return numValue;
  } else if (param.type === 'range') {
    if (
      typeof value === 'object' &&
      value !== null &&
      'start' in value &&
      'end' in value
    ) {
      return value;
    }
    return param.defaultValue;
  }

  return value;
}

// ---------------------------------------------------------------------------
// Parameter code updater
// ---------------------------------------------------------------------------

/**
 * Rewrites the SCAD source string with the new parameter value.
 * Handles all parameter types, preserves inline comments, and normalises
 * multi-line assignments first.
 */
export function updateParameter(code: string, param: Parameter): string {
  // Collapse any multi-line assignment for this parameter onto one line so the
  // single-line replacement regex below can find and rewrite it reliably.
  const normalised = joinMultiLineAssignments(code);
  const escapedName = escapeRegExp(param.name);
  const regex = new RegExp(
    `^\\s*(${escapedName}\\s*=\\s*)[^;]+;([ \\t]*\\/\\/[^\\n]*)?`,
    'm',
  );
  // Default to assuming the type is number
  if (!param.type) {
    return normalised.replace(regex, `$1${param.value};$2`);
  }
  switch (param.type) {
    case 'string':
    case 'file':
    case 'font':
      return normalised.replace(
        regex,
        `$1"${escapeReplacement(escapeQuotes(escapeNewlines(param.value as string)))}";$2`,
      );
    case 'code':
      // Code expressions are stored as quoted strings in the SCAD source.
      // Re-wrap the expression text in quotes, escaping any internal quotes.
      return normalised.replace(
        regex,
        `$1"${escapeReplacement(escapeQuotes(param.value as string))}";$2`,
      );
    case 'number':
      return normalised.replace(regex, `$1${param.value};$2`);
    case 'boolean':
      return normalised.replace(regex, `$1${param.value};$2`);
    case 'string[]':
      return normalised.replace(
        regex,
        `$1[${(param.value as string[])
          .map((value) => escapeReplacement(escapeQuotes(value)))
          .map((value) => `"${value}"`)
          .join(',')}];$2`,
      );
    case 'number[]':
      return normalised.replace(
        regex,
        `$1[${(param.value as number[]).join(',')}];$2`,
      );
    case 'boolean[]':
      return normalised.replace(
        regex,
        `$1[${(param.value as boolean[]).join(',')}];$2`,
      );
    case 'range': {
      const rangeVal = param.value as RangeValue;
      const stepStr = rangeVal.step !== null ? `:${rangeVal.step}` : '';
      return normalised.replace(
        regex,
        `$1[${rangeVal.start}${stepStr}:${rangeVal.end}];$2`,
      );
    }
    case 'color': {
      // Normalize to float array [r, g, b] or [r, g, b, a] regardless of original format.
      // If the stored value is a string (named color / hex string), convert it to a float
      // array first so the output is always a valid OpenSCAD numeric vector.
      let colorArray: number[];
      if (typeof param.value === 'string') {
        const hex = scadStringToHex(param.value) ?? '#000000';
        const alpha = scadStringToAlpha(param.value);
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        const fmt = (n: number) => parseFloat(n.toFixed(3));
        colorArray =
          alpha < 1.0
            ? [fmt(r), fmt(g), fmt(b), fmt(alpha)]
            : [fmt(r), fmt(g), fmt(b)];
      } else {
        colorArray = param.value as number[];
      }
      return normalised.replace(regex, `$1[${colorArray.join(', ')}];$2`);
    }
    case 'material':
      // Material is stored as a 22-element array of material properties
      return normalised.replace(
        regex,
        `$1${serializeMaterialDef(param.value as number[])};$2`,
      );
    case 'svgcheckboxgroup':
      // Write back as a string array of enabled part IDs: ["base", "lid"]
      return normalised.replace(
        regex,
        `$1[${(param.value as string[])
          .map((v) => escapeReplacement(escapeQuotes(v)))
          .map((v) => `"${v}"`)
          .join(', ')}];$2`,
      );
    case 'svgradiogroup':
      // Write back as a quoted string: "simple"
      return normalised.replace(
        regex,
        `$1"${escapeReplacement(escapeQuotes(param.value as string))}";$2`,
      );
    case 'angle':
      // Angle is stored and written back as a plain number — ° is purely a UI annotation.
      return normalised.replace(regex, `$1${param.value};$2`);
    case 'stepper': {
      // The underlying variable is a number or string scalar.
      // Write back the raw value (no quotes for numbers, quotes for strings).
      const sv = param.value;
      if (typeof sv === 'string') {
        return normalised.replace(
          regex,
          `$1"${escapeReplacement(escapeQuotes(sv))}";$2`,
        );
      }
      return normalised.replace(regex, `$1${sv};$2`);
    }
    case 'xypad':
      // Write back as a 2-element numeric vector [x, y].
      return normalised.replace(
        regex,
        `$1[${(param.value as number[]).join(', ')}];$2`,
      );
    case 'xyzpad':
      // Write back as a 3-element numeric vector [x, y, z].
      return normalised.replace(
        regex,
        `$1[${(param.value as number[]).join(', ')}];$2`,
      );
    case 'polygonpad': {
      // Reconstruct [[x0, y0], [x1, y1], ...] from the flat number[] value.
      const flat = param.value as number[];
      const pairs: string[] = [];
      for (let i = 0; i + 1 < flat.length; i += 2) {
        pairs.push(`[${flat[i]}, ${flat[i + 1]}]`);
      }
      return normalised.replace(regex, `$1[${pairs.join(', ')}];$2`);
    }
    case 'grid': {
      // Reconstruct the 2D [[row0...],[row1...],...] SCAD literal from the
      // flat row-major string[] stored in param.value.
      //
      // cols/rows may be $-references (unresolved strings) when updateParameter
      // is called for a sibling parameter change — the grid widget was never
      // touched so gridConfig still holds the raw annotation reference.
      // Falling back to 1 produces single-cell rows, breaking the layout.
      //
      // Resolution strategy (in priority order):
      //   1. gridConfig.cols is already a concrete number — use it directly.
      //   2. gridConfig.cols is a $-ref — infer cols from the existing source
      //      value: count the cells in the first inner row of the normalised
      //      assignment.  This is always correct because the source was written
      //      with the right column count the last time a change was committed.
      //   3. Both dimensions are refs and source parsing fails — infer from
      //      flat.length / rows (using the same fallback chain for rows).
      const flat = param.value as string[];
      const rawCols = param.gridConfig?.cols ?? 1;
      let cols: number;
      if (typeof rawCols === 'number') {
        cols = rawCols;
      } else {
        // Infer from the first inner row in the already-normalised source.
        // The normalised line looks like:
        //   layout = [["a", "b", "c"], ["d", "e", "f"]]; // ...
        // Extract the first [...] after the outer [.
        const firstRowMatch = normalised.match(
          new RegExp(
            `${escapeRegExp(param.name)}\\s*=\\s*\\[\\s*\\[([^\\]]*)\\]`,
          ),
        );
        if (firstRowMatch) {
          // Count quoted tokens in the first row
          const tokens = firstRowMatch[1]
            .split(',')
            .map((s) => s.trim().replace(/^["']|["']$/g, ''))
            .filter((s) => s.length > 0);
          cols = tokens.length || 1;
        } else {
          // Last resort: use rows to divide
          const rawRows = param.gridConfig?.rows ?? 1;
          const rows = typeof rawRows === 'number' ? rawRows : 1;
          cols = rows > 0 ? Math.round(flat.length / rows) : 1;
        }
      }
      cols = Math.max(1, cols);
      const rowArrays: string[] = [];
      for (let r = 0; r < flat.length; r += cols) {
        const rowCells = flat
          .slice(r, r + cols)
          .map((v) => `"${escapeReplacement(escapeQuotes(v))}"`)
          .join(', ');
        rowArrays.push(`[${rowCells}]`);
      }
      return normalised.replace(regex, `$1[${rowArrays.join(', ')}];$2`);
    }
    case 'stack': {
      // Serialise the 1D string[] back to a flat SCAD array literal: ["a", "b", "c"]
      const items = param.value as string[];
      const cells = items
        .map((v) => `"${escapeReplacement(escapeQuotes(v))}"`)
        .join(', ');
      return normalised.replace(regex, `$1[${cells}];$2`);
    }
    default:
      return normalised;
  }
}
